export type FontOption = {
  label: string;
  value: string;
};

/** Default bundled font for book content and style forms. */
export const DEFAULT_FONT_FAMILY = 'Vag';

export type FontFaceSpec = {
  /** Primary CSS font-family name */
  family: string;
  /** File name under `src/fonts` (bundled via `index.css`; keep in sync with `/public`) */
  file: string;
  aliases?: string[];
};

type FontStyleGroup = {
  id: string;
  baseValue: string;
  baseLabel: string;
  variants: {
    regular: string;
    bold: string;
    italic?: string;
    boldItalic?: string;
  };
};

/** Bundled OTF files in `src/fonts` (see `src/fonts/faces.css`). */
export const PUBLIC_FONT_FACES: FontFaceSpec[] = [
  { family: 'Vag', file: 'Vag-Regular.otf', aliases: ['vag-regular', 'vag'] },
  { family: 'Vag-Bold', file: 'Vag-Bold.otf', aliases: ['vag-bold'] },
  { family: 'Vag-Italic', file: 'Vag-Italic.otf', aliases: ['vag-italic'] },
  { family: 'Vag-BoldItalic', file: 'Vag-BoldItalic.otf', aliases: ['vag-bolditalic'] },
  { family: 'knswami', file: 'knswami-Regular.otf', aliases: ['knswami-regular'] },
  { family: 'knswami-bold', file: 'knswami-Bold.otf' },
];

const DEVANAGARI_FALLBACK = '"Kohinoor Devanagari","Noto Sans Devanagari","Mukta",sans-serif';

/** Stored editor / override token (case-insensitive) → CSS font-family stack. */
const FONT_STACK_BY_KEY: Record<string, string> = {
  vag: `"Vag","vag-regular","vag",${DEVANAGARI_FALLBACK}`,
  'vag-regular': `"vag-regular","Vag","vag",${DEVANAGARI_FALLBACK}`,
  'vag-bold': `"Vag-Bold","vag-bold",${DEVANAGARI_FALLBACK}`,
  'vag-italic': `"Vag-Italic","vag-italic",${DEVANAGARI_FALLBACK}`,
  'vag-bolditalic': `"Vag-BoldItalic","vag-bolditalic",${DEVANAGARI_FALLBACK}`,
  knswami: `"knswami","knswami-regular",${DEVANAGARI_FALLBACK}`,
  'knswami-regular': `"knswami-regular","knswami",${DEVANAGARI_FALLBACK}`,
  'knswami-bold': `"knswami-bold","knswami",${DEVANAGARI_FALLBACK}`,
  serif: 'Georgia, "Times New Roman", serif',
  'sans-serif': 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  sans: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  monospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
  'kohinoor devanagari': `"Kohinoor Devanagari","Noto Sans Devanagari","Mukta",sans-serif`,
  'noto sans devanagari': `"Noto Sans Devanagari","Mukta",sans-serif`,
  mukta: `"Mukta","Noto Sans Devanagari",sans-serif`,
};

export function normalizeFontToken(ff: unknown): string {
  return typeof ff === 'string' ? ff.trim().toLowerCase() : '';
}

const FONT_STYLE_GROUPS: FontStyleGroup[] = [
  {
    id: 'vag',
    baseValue: 'Vag',
    baseLabel: 'Vag',
    variants: {
      regular: 'Vag',
      bold: 'Vag-Bold',
      italic: 'Vag-Italic',
      boldItalic: 'Vag-BoldItalic',
    },
  },
  {
    id: 'knswami',
    baseValue: 'knswami',
    baseLabel: 'KNSwami',
    variants: {
      regular: 'knswami',
      bold: 'knswami-bold',
    },
  },
];

const FONT_STYLE_GROUP_BY_TOKEN = new Map<string, FontStyleGroup>();
const VARIANT_KIND_BY_TOKEN = new Map<string, 'regular' | 'bold' | 'italic' | 'boldItalic'>();

function registerFontStyleToken(
  token: string | undefined,
  group: FontStyleGroup,
  kind: 'regular' | 'bold' | 'italic' | 'boldItalic',
) {
  if (!token) return;
  const key = normalizeFontToken(token);
  FONT_STYLE_GROUP_BY_TOKEN.set(key, group);
  VARIANT_KIND_BY_TOKEN.set(key, kind);
}

for (const group of FONT_STYLE_GROUPS) {
  registerFontStyleToken(group.baseValue, group, 'regular');
  registerFontStyleToken(group.variants.regular, group, 'regular');
  registerFontStyleToken(group.variants.bold, group, 'bold');
  registerFontStyleToken(group.variants.italic, group, 'italic');
  registerFontStyleToken(group.variants.boldItalic, group, 'boldItalic');
  if (group.id === 'vag') {
    registerFontStyleToken('vag', group, 'regular');
    registerFontStyleToken('vag-regular', group, 'regular');
    registerFontStyleToken('vag-bold', group, 'bold');
    registerFontStyleToken('vag-italic', group, 'italic');
    registerFontStyleToken('vag-bolditalic', group, 'boldItalic');
  }
  if (group.id === 'knswami') {
    registerFontStyleToken('knswami-regular', group, 'regular');
    registerFontStyleToken('knswami-bold', group, 'bold');
  }
}

/** Tokens whose weight/style is carried by dedicated font files (not CSS fontWeight/fontStyle). */
const DEDICATED_FACE_KEYS = new Set<string>([
  'vag',
  'vag-regular',
  'vag-bold',
  'vag-italic',
  'vag-bolditalic',
  'knswami',
  'knswami-regular',
  'knswami-bold',
]);

export const FONT_FAMILY_OPTIONS: FontOption[] = [
  ...FONT_STYLE_GROUPS.map((group) => ({ label: group.baseLabel, value: group.baseValue })),
  { label: 'Kohinoor Devanagari', value: 'Kohinoor Devanagari' },
  { label: 'Noto Sans Devanagari', value: 'Noto Sans Devanagari' },
  { label: 'Mukta', value: 'Mukta' },
  { label: 'Serif', value: 'serif' },
  { label: 'Sans', value: 'sans-serif' },
  { label: 'Monospace', value: 'monospace' },
];

const BUNDLED_FONT_FACE_LABELS: Record<string, string> = {
  Vag: 'Vag',
  'Vag-Bold': 'Vag Bold',
  'Vag-Italic': 'Vag Italic',
  'Vag-BoldItalic': 'Vag Bold Italic',
  knswami: 'KNSwami',
  'knswami-bold': 'KNSwami Bold',
};

const BUNDLED_FONT_FAMILIES = new Set(PUBLIC_FONT_FACES.map((face) => face.family));

export function resolveFontStack(ff?: string): string | undefined {
  const raw = typeof ff === 'string' ? ff.trim() : '';
  if (!raw) return undefined;
  return FONT_STACK_BY_KEY[normalizeFontToken(raw)] ?? raw;
}

/** CSS font-family value safe for Jodit list keys (no double quotes — they break dropdown HTML). */
export const toJoditFontFamilyKey = (stack: string): string => stack.replace(/"/g, '').trim();

/** Font family options for Jodit toolbar (key = CSS font-family, value = label). */
export function buildJoditFontFamilyList(): Record<string, string> {
  const list: Record<string, string> = {
    '': 'Default',
  };

  for (const face of PUBLIC_FONT_FACES) {
    list[face.family] = BUNDLED_FONT_FACE_LABELS[face.family] ?? face.family;
  }

  for (const option of FONT_FAMILY_OPTIONS) {
    if (BUNDLED_FONT_FAMILIES.has(option.value)) continue;
    const stack = resolveFontStack(option.value);
    if (stack) {
      list[toJoditFontFamilyKey(stack)] = option.label;
    }
  }

  return {
    ...list,
    'Times New Roman, Times, serif': 'Times New Roman',
    'Arial, Helvetica, sans-serif': 'Arial',
    'Georgia, Palatino, serif': 'Georgia',
    'Tahoma, Geneva, sans-serif': 'Tahoma',
    'Trebuchet MS, Helvetica, sans-serif': 'Trebuchet MS',
    'Verdana, Geneva, sans-serif': 'Verdana',
  };
}

export function findFontStyleGroup(ff: unknown): FontStyleGroup | undefined {
  const key = normalizeFontToken(ff);
  if (!key) return undefined;
  return FONT_STYLE_GROUP_BY_TOKEN.get(key);
}

export function isDedicatedFontFamilyBase(ff: unknown): boolean {
  return Boolean(findFontStyleGroup(ff));
}

/** Editor select value — maps stored variant tokens back to one base family option. */
export function getBaseFontFamilyToken(ff: unknown): string | undefined {
  const group = findFontStyleGroup(ff);
  if (group) return group.baseValue;
  const raw = typeof ff === 'string' ? ff.trim() : '';
  return raw || undefined;
}

export function parseFontFamilyVariant(ff: unknown): { bold: boolean; italic: boolean } {
  const key = normalizeFontToken(ff);
  const kind = VARIANT_KIND_BY_TOKEN.get(key);
  if (!kind) return { bold: false, italic: false };
  return {
    bold: kind === 'bold' || kind === 'boldItalic',
    italic: kind === 'italic' || kind === 'boldItalic',
  };
}

export function isDedicatedBoldRegularFamily(ff: unknown): boolean {
  const parsed = parseFontFamilyVariant(ff);
  const group = findFontStyleGroup(ff);
  return !!group && !parsed.bold;
}

export function isDedicatedBoldFaceFamily(ff: unknown): boolean {
  const parsed = parseFontFamilyVariant(ff);
  const group = findFontStyleGroup(ff);
  return !!group && parsed.bold && !parsed.italic;
}

export function usesDedicatedFontFileForWeight(ff: unknown): boolean {
  return DEDICATED_FACE_KEYS.has(normalizeFontToken(ff));
}

export function isBoldFromFontFamily(ff: unknown): boolean | undefined {
  const group = findFontStyleGroup(ff);
  if (!group) return undefined;
  return parseFontFamilyVariant(ff).bold;
}

export function isItalicFromFontFamily(ff: unknown): boolean | undefined {
  const group = findFontStyleGroup(ff);
  if (!group) return undefined;
  const parsed = parseFontFamilyVariant(ff);
  if (group.variants.italic) return parsed.italic;
  return undefined;
}

export function applyFontStyleToFontFamily(
  currentFontFamily: unknown,
  style: { bold: boolean; italic: boolean },
): { fontFamily: string; handled: true; italicUsesFontFile: boolean } | { handled: false } {
  const group = findFontStyleGroup(currentFontFamily) ?? findFontStyleGroup(getBaseFontFamilyToken(currentFontFamily));
  if (!group) return { handled: false };

  const { bold, italic } = style;
  const { variants } = group;

  if (bold && italic && variants.boldItalic) {
    return { fontFamily: variants.boldItalic, handled: true, italicUsesFontFile: true };
  }
  if (bold) {
    return { fontFamily: variants.bold, handled: true, italicUsesFontFile: false };
  }
  if (italic && variants.italic) {
    return { fontFamily: variants.italic, handled: true, italicUsesFontFile: true };
  }
  return { fontFamily: variants.regular, handled: true, italicUsesFontFile: false };
}

/** Swap regular ↔ dedicated bold font files when toggling bold in the editor. */
export function applyDedicatedBoldToggleToFontFamily(
  currentFontFamily: unknown,
  boldOn: boolean,
): { fontFamily: string; handled: true } | { handled: false } {
  const italic = parseFontFamilyVariant(currentFontFamily).italic;
  const result = applyFontStyleToFontFamily(currentFontFamily, { bold: boldOn, italic });
  if (!result.handled) return { handled: false };
  return { fontFamily: result.fontFamily, handled: true };
}

export function resolveStoredTypographyFromForm(fields: {
  fontFamily?: unknown;
  bold?: unknown;
  italic?: unknown;
  fontWeight?: unknown;
}): {
  fontFamily?: string;
  fontWeight?: number;
  fontStyle?: 'normal' | 'italic';
} {
  const rawFamily = typeof fields.fontFamily === 'string' ? fields.fontFamily.trim() : '';
  const weight = Number(fields.fontWeight);
  const hasExplicitWeight =
    fields.fontWeight != null && fields.fontWeight !== '' && Number.isFinite(weight);
  const bold = Boolean(fields.bold) || (hasExplicitWeight && weight >= 600);
  const italic = Boolean(fields.italic);

  if (rawFamily) {
    const base = getBaseFontFamilyToken(rawFamily) ?? rawFamily;
    const styled = applyFontStyleToFontFamily(base, { bold, italic });
    if (styled.handled) {
      const group = findFontStyleGroup(styled.fontFamily);
      const italicInFile = Boolean(group?.variants.italic) && styled.italicUsesFontFile;
      return {
        fontFamily: styled.fontFamily,
        fontStyle: italicInFile ? 'normal' : italic ? 'italic' : 'normal',
      };
    }

    return {
      fontFamily: base,
      fontWeight: hasExplicitWeight ? weight : bold ? 700 : 400,
      fontStyle: italic ? 'italic' : 'normal',
    };
  }

  if (hasExplicitWeight || bold || italic) {
    return {
      fontWeight: hasExplicitWeight ? weight : bold ? 700 : 400,
      fontStyle: italic ? 'italic' : 'normal',
    };
  }

  return {};
}
